export type AnimData = {
    v: string; // version
    fr: number; // frame rate
    ip: number; // in point (initial frame)
    op: number; // out point (final frame)
    w: number; // width
    h: number; // height
    nm: string; // name
    ddd: number;
    assets: AnimAsset[];
    layers: AnimLayer[];
    markers: any[];
};

export type AnimAsset = {
    id: string; // id
    p: string; // filename (key name)
    layers?: AnimLayer[]; // animation layers, if comp asset,
    w: number; //width
    h: number; //height
};

export type AnimLayer = {
    ddd: number; // 3d layer
    ind: number; // layer index in AE, used for parenting and expressions
    ty: number; // type (2 = image, 0 = comp?)
    nm: string; // AE layer name
    cl: string; // class: parsed layer name used as html class in html renderer
    refId: string; // id pointing to source image in AnimAssets object
    sr: number; // time stretching (always 1 for us i hope)
    ks: AnimTransform; // transform properties
    ao: number; // auto-orient along path AE property (default 0)
    ip: number; // in point (initial frame)
    op: number; // out point (final frame)
    st: number; // start time
    bm: number; // blend mode (default 0)
    parent?: number; // layer parent (ind of parent)
    hasMask?: boolean;
    masksProperties?: [AnimMasksProperties]; // masking
};

export type AnimTransform = {
    o: Value; // opacity
    r: Value; // rotation
    p: MultiDim; // position
    a: MultiDim; // anchor point
    s: MultiDim; // scale
};

export type AnimMasksProperties = {
    inv: boolean; // inverted mask
    mode: string; // mask mode (should always be "a" i hope)
    pt: Shape; // mask vertices (properties/shape or properties/shapeKeyframed)
    o: Value; // opacity
    x: any; // the mystery property
    nm: string; // mask name
};

type Shape = {
    a: number;
    ix: number;
    k: ShapeProperty | ShapePropertyKeyframe[];
};

class ShapePropertyKeyframe {
    //https://github.com/airbnb/lottie-web/blob/master/docs/json/properties/shapePropKeyframe.json
    s: ShapeProperty[];
    t: number; //time
    i?: [number, number][]; // bezier in pts
    o?: [number, number][]; // bezier out pts
}

class ShapeProperty {
    c: boolean; // closed
    i: [number, number][]; // bezier in pts
    o: [number, number][]; // bezier out pts
    v: [number, number][]; // vertices
}

type Value = {
    a: number;
    ix: number;
    k: number | AnimKeyframe<[number]>[];
};

type KeyframedAnimProperty<T> = {
    k: AnimKeyframe<T>[];
    ix: string;
};

type AnimKeyframe<T> = {
    s: T;
    t: number;
    i?: { x: T; y: T }; // bezier curve interpolation in value
    o?: { x: T; y: T }; // bezier curve interpolation out value
    ti?: number[]; // in spatial tangent
    to?: number[]; // out spatial tangent
};

type MultiDim = KeyframedAnimProperty<number[]>;

type Vector2 = { x: number; y: number };

type GoodKeyframe<T> = {
    startFrame: number;
    endFrame: number;
    start: T;
    end: T;
    tweenIn?: { x: number; y: number };
    tweenOut?: { x: number; y: number };
};

class LottieFactory {
    public static spawnAnimationLayers(
        scene: Phaser.Scene,
        anim: LottieAnimation,
        layers: AnimLayer[],
        container: Phaser.GameObjects.Container
    ): LottieLayer[] {
        const name = anim.name;
        const animData = anim.animData;
        const spritesheet = anim.getSpritesheet();
        const hasSpritesheet = anim.hasSpritesheet();

        const childLayers: LottieLayer[] = [];

        const allLayers: LottieLayer[] = [];
        for (let i = layers.length - 1; i >= 0; i--) {
            const layerData = layers[i];
            if (layerData.hasMask) console.warn("Animation " + name + " uses masking, will appear wrong in game.");

            let asset: Phaser.GameObjects.Image | Phaser.GameObjects.Container | Phaser.GameObjects.Graphics = null;
            const compChildLayers: LottieLayer[] = [];
            if (layerData.ty == 2) {
                // image
                const assetData = animData.assets.find((a) => a.id == layerData.refId);
                const texture = hasSpritesheet ? spritesheet : name + "-" + assetData.p;
                const frame = hasSpritesheet ? assetData.p : null;
                asset = scene.add.image(0, 0, texture, frame);
                asset.setOrigin(0, 0);
            } else if (layerData.ty == 0) {
                // comp
                const assetData = animData.assets.find((a) => a.id == layerData.refId);
                asset = scene.add.container(0, 0);
                asset.width = assetData.w;
                asset.height = assetData.h;
                const compLayers = LottieFactory.spawnAnimationLayers(scene, anim, assetData.layers, asset);
                compChildLayers.push(...compLayers);
                allLayers.push(...compLayers);
            } else if (layerData.ty == 4) {
                //shape layer
                asset = scene.add.graphics();
            }

            if (asset == null) {
                asset = scene.add.graphics();
            }

            const layer = new LottieLayer(scene, i, { x: 0, y: 0 }, asset, layerData);
            if (layerData.parent == null) {
                container.add(layer);
                childLayers.push(layer);
            }
            compChildLayers.forEach((childLayer) => {
                layer.addChild(childLayer);
                layer.addTimelineChild(childLayer);
            });
            allLayers.push(layer);
        }

        for (const layer of allLayers) {
            if (layer.layerData.parent) {
                const parent = allLayers.find((l) => l.layerData.ind == layer.layerData.parent);
                if (parent != null) {
                    parent.addChild(layer);
                    if (parent.timelineParent && layer.timelineParent == null) {
                        console.log("Might be unused");
                        parent.timelineParent.addTimelineChild(layer);
                    }
                }
            }
        }

        return childLayers;
    }
}

export default class LottieAnimation extends Phaser.GameObjects.Container {
    name: string;
    animData: AnimData;
    currentFrame = 0;
    frameRate: number;
    inFrame: number;
    outFrame: number;
    frameTime: number;
    playing = false;
    loop = false;
    isPaused = false;
    speed = 1;
    endFrame: number;
    loopStart: number;

    layers: LottieLayer[] = [];

    events: Phaser.Events.EventEmitter;
    static readonly onLoopEvent = "LOTTIE_ANIM_LOOP";
    static readonly onFinishIntro = "LOTTIE_ANIM_INTRO_FINISH";
    static readonly onFinish = "LOTTIE_ANIM_FINISH";
    static readonly onFrame = "LOTTIE_ANIM_FRAME";

    getSpritesheet(): string {
        return this.name + "-atlas";
    }

    hasSpritesheet(): boolean {
        const spritesheet = this.getSpritesheet();
        return this.scene.textures.exists(spritesheet);
    }

    constructor(scene: Phaser.Scene, x: number, y: number, name: string, autoplay: boolean, loop: boolean) {
        super(scene, x, y);
        this.name = name;
        this.animData = scene.cache.json.get(this.name + "-animData") as AnimData;

        this.setSize(this.animData.w, this.animData.h);

        this.frameRate = this.animData.fr;
        this.inFrame = this.animData.ip;
        this.outFrame = this.animData.op;
        this.endFrame = this.outFrame;
        this.loopStart = this.inFrame;
        this.frameTime = 1 / this.frameRate;

        const layers = this.animData.layers;

        const offsetContainer = this.scene.add.container(0, 0);
        this.add(offsetContainer);
        offsetContainer.x -= this.animData.w / 2;
        offsetContainer.y -= this.animData.h / 2;

        const lottieLayers = LottieFactory.spawnAnimationLayers(this.scene, this, layers, offsetContainer);
        lottieLayers.forEach((layer) => {
            if (layer.parentContainer == offsetContainer) this.layers.push(layer);
        });

        this.events = new Phaser.Events.EventEmitter();

        scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
        scene.events.on(Phaser.Scenes.Events.SHUTDOWN, this.removeListeners, this);

        this.currentFrame = this.inFrame;
        this.loop = loop;
        if (autoplay) this.play();
    }

    removeListeners(): void {
        if (this.scene) {
            this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
            this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.removeListeners, this);
        }
    }

    destroy(): void {
        this.isPaused = true;
        this.playing = false;
        this.removeListeners();
        super.destroy();
    }

    play(): void {
        this.isPaused = false;
        this.playing = true;
        this.endFrame = this.outFrame;
        this.updateFrame(this.currentFrame);
    }

    stop(): void {
        this.isPaused = true;
        this.playing = false;
    }

    pause(): void {
        this.isPaused = true;
        this.playing = false;
    }

    public playSegment(start: number, end: number): void {
        this.goToAndPlay(start, true);
        this.endFrame = end;
    }

    public goToAndPlay(value: number, isFrame = true): void {
        const frame = isFrame ? value : value * this.frameRate;
        this.currentFrame = frame;
        this.playing = true;
        this.endFrame = this.outFrame;
        this.updateFrame(this.currentFrame);
    }

    setLoopStart(loopStart: number): void {
        this.loopStart = loopStart;
    }

    setSpeed(speed: number): void {
        this.speed = speed;
    }

    public goToAndStop(value: number, isFrame = true): void {
        const frame = isFrame ? value : value * this.frameRate;
        this.currentFrame = frame;
        this.playing = false;
        this.endFrame = this.outFrame;
        this.updateFrame(this.currentFrame);
    }

    update(_t: number, dt: number): void {
        dt /= 1000;
        dt *= this.speed;

        if (this.playing && !this.isPaused) {
            const inIntro = this.currentFrame < this.loopStart;
            this.currentFrame += dt * this.frameRate;
            if (this.currentFrame > this.endFrame) {
                if (this.loop && this.endFrame == this.outFrame) {
                    this.currentFrame = this.loopStart;
                    this.events.emit(LottieAnimation.onLoopEvent);
                }
                else {
                    this.playing = false;
                    this.events.emit(LottieAnimation.onFinish);
                }
            } else if (inIntro && this.currentFrame >= this.loopStart) {
                this.events.emit(LottieAnimation.onFinishIntro);
            }
            //this.updateFrame(this.currentFrame); //if we update here, we end up with masking problems
        }
        this.updateFrame(this.currentFrame);
    }

    updateFrame(t: number): void {
        for (const layer of this.layers) {
            layer.update(t);
        }
        this.events.emit(LottieAnimation.onFrame);
    }
}

class LottieLayer extends Phaser.GameObjects.Container {
    id: number;
    asset: Phaser.GameObjects.Image | Phaser.GameObjects.Container | Phaser.GameObjects.Graphics;
    maskGraphic: Phaser.GameObjects.Graphics;
    layerData: AnimLayer;
    order: number;
    offset: Vector2;

    inFrame: number;
    outFrame: number;
    startFrame: number;

    stretch: number;

    childLayers: LottieLayer[] = [];
    timelineChildren: LottieLayer[] = [];
    timelineParent: LottieLayer;

    positionFrames: GoodKeyframe<Vector2>[] = [];
    anchorFrames: GoodKeyframe<Vector2>[] = [];
    scaleFrames: GoodKeyframe<Vector2>[] = [];
    rotationFrames: GoodKeyframe<number>[] = [];
    alphaFrames: GoodKeyframe<number>[] = [];

    constructor(
        scene: Phaser.Scene,
        order: number,
        offset: Vector2,
        asset: Phaser.GameObjects.Image | Phaser.GameObjects.Container | Phaser.GameObjects.Graphics,
        layerData: AnimLayer
    ) {
        super(scene);
        this.order = order;
        this.offset = offset;
        this.layerData = layerData;
        this.positionFrames = AnimDataConverter.convertVector2(layerData.ks.p);
        this.anchorFrames = AnimDataConverter.convertVector2(layerData.ks.a);
        this.scaleFrames = AnimDataConverter.convertVector2(layerData.ks.s);
        this.rotationFrames = AnimDataConverter.convertValue(layerData.ks.r);
        this.alphaFrames = AnimDataConverter.convertValue(layerData.ks.o);
        this.inFrame = layerData.ip;
        this.startFrame = layerData.st;
        this.outFrame = layerData.op;
        this.stretch = layerData.sr;
        this.asset = asset;
        this.add(asset);
        //asset.visible = false;

        if (layerData.hasMask) {
            this.maskGraphic = scene.add.graphics({ x: 0, y: 0, fillStyle: { color: 0xff0000, alpha: 1 } });
            this.drawShapeMask(0);
            this.add(this.maskGraphic);
            const geometryMask = this.createGeometryMask(this.maskGraphic);
            geometryMask.setInvertAlpha(this.layerData.masksProperties[0].inv);
            this.maskGraphic.visible = false;
            this.setMask(geometryMask);
        }

        // if (mask != null) asset.setMask(mask);
    }

    addChild(layer: LottieLayer): void {
        // console.log("Add " + layer.layerData.nm + " as transform child of " + this.layerData.nm);
        this.add(layer);
        this.childLayers.push(layer);

        const objects = [this.asset, ...this.childLayers];
        const sorted = objects.sort((a, b) => {
            let ax = 0;
            let bx = 0;

            if (a instanceof LottieLayer) ax = a.order;
            else ax = this.order;
            if (b instanceof LottieLayer) bx = b.order;
            else bx = this.order;
            return bx - ax;
        });

        for (let i = 0; i < sorted.length; i++) {
            this.moveTo(sorted[i], i);
        }
    }

    addTimelineChild(layer: LottieLayer): void {
        // console.log("Add " + layer.layerData.nm + " to timeline of " + this.layerData.nm);
        this.timelineChildren.push(layer);
        layer.timelineParent = this;

        layer.childLayers.forEach((childLayer) => {
            if (childLayer.timelineParent == null) this.addTimelineChild(childLayer); //ordering issues in AE?
        });
    }

    update(t: number): void {
        t /= this.stretch;

        this.asset.visible = this.inFrame <= t && this.outFrame >= t;
        if (this.asset.visible) {
            const pos = this.getVector2(t, this.positionFrames);
            this.x = pos.x + this.offset.x;
            this.y = pos.y + this.offset.y;

            if (this.parentContainer != null && this.parentContainer instanceof LottieLayer) {
                this.x += this.parentContainer.asset.x;
                this.y += this.parentContainer.asset.y;
            }

            const anchor = this.getVector2(t, this.anchorFrames);
            this.asset.x = -anchor.x;
            this.asset.y = -anchor.y;

            const scale = this.getVector2(t, this.scaleFrames);
            this.scaleX = scale.x / 100;
            this.scaleY = scale.y / 100;

            this.angle = this.getValue(t, this.rotationFrames);
            this.alpha = this.getValue(t, this.alphaFrames) / 100;

            if (this.layerData.hasMask) {
                const animatedMask = this.layerData.masksProperties[0].pt.k instanceof Array;
                if (animatedMask) {
                    this.drawShapeMask(t);
                }
            }
            if (this.maskGraphic != null) {
                const worldTransform = this.asset.getWorldTransformMatrix();
                this.maskGraphic.scaleX = worldTransform.scaleX;
                this.maskGraphic.scaleY = worldTransform.scaleY;
                this.maskGraphic.x = worldTransform.tx;
                this.maskGraphic.y = worldTransform.ty;
            }
        }

        for (const layer of this.timelineChildren) {
            layer.update(t);
        }
    }

    drawShapeMask(t: number): void {
        const maskProps = this.layerData.masksProperties[0];
        const shapeProperties: ShapeProperty[] = [];
        if (maskProps.pt.k instanceof Array) {
            const keyframes = maskProps.pt.k as ShapePropertyKeyframe[];
            const firstFrame = keyframes[0].t;
            const lastFrame = keyframes[keyframes.length - 1].t;
            t = Phaser.Math.Clamp(t, firstFrame, lastFrame);
            let keyframe = null;
            for (let i = 0; i < keyframes.length; i++) {
                if (t < keyframes[i].t && keyframe == null) {
                    keyframe = keyframes[i - 1];
                    i = keyframes.length;
                }
            }
            if (keyframe == null) keyframe = keyframes[keyframes.length - 1];
            const keyframeIndex = keyframes.indexOf(keyframe);
            const endFrame = keyframes[Math.min(keyframeIndex + 1, keyframes.length - 1)];
            const properties = keyframe.s;
            let progress = (t - keyframe.t) / (endFrame.t - keyframe.t);
            if (isNaN(progress)) progress = 1;
            for (let i = 0; i < properties.length; i++) {
                const lerpedProp = new ShapeProperty();
                lerpedProp.v = [];
                lerpedProp.i = [];
                lerpedProp.o = [];
                for (let j = 0; j < properties[i].v.length; j++) {
                    const startProp = keyframe.s[i].v;
                    const endProp = endFrame.s[i].v;
                    lerpedProp.v.push([startProp[j][0], startProp[j][1]]);

                    const startPropIn = keyframe.s[i].i;
                    const endPropIn = endFrame.s[i].i;
                    lerpedProp.i.push([startPropIn[j][0], endPropIn[j][1]]);

                    const startPropOut = keyframe.s[i].o;
                    const endPropOut = endFrame.s[i].o;
                    lerpedProp.o.push([startPropOut[j][0], endPropOut[j][1]]);

                    lerpedProp.v[j][0] = Phaser.Math.Linear(startProp[j][0], endProp[j][0], progress);
                    lerpedProp.v[j][1] = Phaser.Math.Linear(startProp[j][1], endProp[j][1], progress);

                    lerpedProp.i[j][0] = Phaser.Math.Linear(startPropIn[j][0], endPropIn[j][0], progress);
                    lerpedProp.i[j][1] = Phaser.Math.Linear(startPropIn[j][1], endPropIn[j][1], progress);

                    lerpedProp.o[j][0] = Phaser.Math.Linear(startPropOut[j][0], endPropOut[j][0], progress);
                    lerpedProp.o[j][1] = Phaser.Math.Linear(startPropOut[j][1], endPropOut[j][1], progress);
                }
                shapeProperties.push(lerpedProp);
            }
        } else {
            const shapeProperty = maskProps.pt.k as ShapeProperty;
            shapeProperties.push(shapeProperty);
        }
        let path: Phaser.Curves.Path = null;

        shapeProperties.forEach((shapeProp) => {
            const pts = shapeProp.v;
            const ins = shapeProp.i;
            const outs = shapeProp.o;
            if (path == null) path = new Phaser.Curves.Path(pts[0][0], pts[0][1]);
            //Reference: https://github.com/airbnb/lottie-web/blob/699eb219f32a80718e8dc57ba18848fe2250d165/player/js/elements/canvasElements/CVMaskElement.js
            for (let z = 1; z < pts.length; z++) {
                const pt = pts[z];
                const inPts = ins[z];
                const outPts = outs[z - 1];
                path.cubicBezierTo(pt[0], pt[1], pts[z - 1][0] + outPts[0], pts[z - 1][1] + outPts[1], pt[0] + inPts[0], pt[1] + inPts[1]);
            }
            path.cubicBezierTo(
                pts[0][0],
                pts[0][1],
                pts[pts.length - 1][0] + outs[pts.length - 1][0],
                pts[pts.length - 1][1] + outs[pts.length - 1][1],
                pts[0][0] + ins[0][0],
                pts[0][1] + ins[0][1]
            );
        });

        if (path != null) {
            path.closePath();
            this.maskGraphic.clear();
            this.maskGraphic.fillStyle(0xff0000, 1.0);
            this.maskGraphic.fillPoints(path.getPoints(), true, true);
        }
    }

    getVector2(t: number, frames: GoodKeyframe<Vector2>[]): { x: number; y: number } {
        const firstFrame = frames[0].startFrame;
        const lastFrame = frames[frames.length - 1].endFrame;
        t = Phaser.Math.Clamp(t, firstFrame, lastFrame);
        let keyframe = frames.find((frame) => t >= frame.startFrame && t <= frame.endFrame);
        if (keyframe == null) {
            keyframe = frames[frames.length - 1];
        } //todo: invalid numbers are getting passed in
        const progress = (t - keyframe.startFrame) / (keyframe.endFrame - keyframe.startFrame);
        const x = Phaser.Math.Linear(keyframe.start.x, keyframe.end.x, progress);
        const y = Phaser.Math.Linear(keyframe.start.y, keyframe.end.y, progress);
        return { x: x, y: y };
    }

    getValue(t: number, frames: GoodKeyframe<number>[]): number {
        const firstFrame = frames[0].startFrame;
        const lastFrame = frames[frames.length - 1].endFrame;
        t = Phaser.Math.Clamp(t, firstFrame, lastFrame);
        let keyframe = frames.find((frame) => t >= frame.startFrame && t <= frame.endFrame);
        if (keyframe == null) keyframe = keyframe = frames[frames.length - 1]; //todo: invalid numbers are getting passed in
        const progress = (t - keyframe.startFrame) / (keyframe.endFrame - keyframe.startFrame);
        return Phaser.Math.Linear(keyframe.start, keyframe.end, progress);
    }
}

class AnimDataConverter {
    static convertVector2(position: KeyframedAnimProperty<number[]>): GoodKeyframe<Vector2>[] {
        const outputFrames: GoodKeyframe<Vector2>[] = [];

        const keyframes = position.k;

        if (typeof keyframes[0] == "object" && "t" in keyframes[0]) {
            for (let i = 0; i < keyframes.length; i++) {
                const frame = keyframes[i];
                const startFrame = frame.t;
                let endFrame = frame.t + 1;
                const startPos = { x: frame.s[0], y: frame.s[1] };
                let endPos = { x: frame.s[0], y: frame.s[1] };
                const tweenIn = { x: 0, y: 0 };
                const tweenOut = { x: 0, y: 0 };

                if (frame.ti) {
                    tweenIn.x = frame.ti[0];
                    tweenIn.y = frame.ti[1];
                }
                if (frame.to) {
                    tweenOut.x = frame.to[0];
                    tweenOut.y = frame.to[1];
                }

                if (i + 1 < keyframes.length) {
                    const nextFrame = keyframes[i + 1];
                    endPos = { x: nextFrame.s[0], y: nextFrame.s[1] };
                    endFrame = nextFrame.t;
                }

                outputFrames[i] = {
                    startFrame: startFrame,
                    endFrame: endFrame,
                    start: startPos,
                    end: endPos,
                    tweenIn: tweenIn,
                    tweenOut: tweenOut,
                };
            }
        } else {
            const posArray = (<any>keyframes) as number[];
            const pos = { x: posArray[0], y: posArray[1] };
            outputFrames[0] = { startFrame: 0, endFrame: 1, start: pos, end: pos, tweenIn: { x: 0, y: 0 }, tweenOut: { x: 0, y: 0 } };
        }

        return outputFrames;
    }

    static convertValue(rotation: Value): GoodKeyframe<number>[] {
        const outputFrames: GoodKeyframe<number>[] = [];

        const keyframes = rotation.k;

        if (typeof keyframes == "object") {
            for (let i = 0; i < keyframes.length; i++) {
                const frame = keyframes[i];
                const startFrame = frame.t;
                let endFrame = frame.t + 1;
                const startRot = frame.s[0];
                let endRot = frame.s[0];
                const tweenIn = { x: 0, y: 0 };
                const tweenOut = { x: 0, y: 0 };

                if (frame.ti) {
                    tweenIn.x = frame.ti[0];
                    tweenIn.y = frame.ti[1];
                }
                if (frame.to) {
                    tweenOut.x = frame.to[0];
                    tweenOut.y = frame.to[1];
                }

                if (i + 1 < keyframes.length) {
                    const nextFrame = keyframes[i + 1];
                    endRot = nextFrame.s[0];
                    endFrame = nextFrame.t;
                }

                outputFrames[i] = {
                    startFrame: startFrame,
                    endFrame: endFrame,
                    start: startRot,
                    end: endRot,
                    tweenIn: tweenIn,
                    tweenOut: tweenOut,
                };
            }
        } else {
            const rot = (<any>keyframes) as number;
            outputFrames[0] = { startFrame: 0, endFrame: 1, start: rot, end: rot, tweenIn: { x: 0, y: 0 }, tweenOut: { x: 0, y: 0 } };
        }

        return outputFrames;
    }
}
